import { Feature, Geometry, Point } from 'geojson';
import { ComponentProps, useMemo } from 'react';
import { Layer, Source } from 'react-map-gl/mapbox';
import useAuthorization from '@alltrails/context/hooks/useAuthorization';
import type { POISearchResult } from '@alltrails/types/AlgoliaResults';
import useMap from '../../../hooks/useMap';
import { useAppSelector } from '../../../redux';
import { defaultPOIPinLayerPaintProps, getLayerBeforeId, poiPinsId, poiPopupPinId } from '../../../utils/layers';
import { coordinatesToSerializedCoordinates } from '../../../utils/serializedCoordinates';
import { defaultPOIPinXSLayoutProps } from './constants';
import POIPopup from './POIPopup';
import type POIResultProperties from './POIResultProperties';

const poiPopupSourceId = 'poi-popup-source';

type POIPopupPinProps = {
  getPOIUrl?: (poi: POISearchResult) => string;
  onPopupClick?: (poi: POISearchResult) => void;
  onMouseEnter?: (...args: any[]) => void;
  onMouseLeave?: (...args: any[]) => void;
  onPOICardViewed?: ComponentProps<typeof POIPopup>['onPOICardViewed'];
};

const POIPopupPin = ({ getPOIUrl, onPopupClick, onMouseEnter, onMouseLeave, onPOICardViewed }: POIPopupPinProps) => {
  const map = useMap();
  const { hasPermission } = useAuthorization();
  const { activePOI, hoveredPOI, showPOIPopupMenu } = useAppSelector(state => ({
    activePOI: state.map.activePOI,
    hoveredPOI: state.map.hoveredPOI,
    showPOIPopupMenu: state.map.showPOIPopupMenu
  }));
  const hasLocationsManagePermission = hasPermission({ permission: 'locations:manage' });
  // The popup menu takes the place of the hover card for the POI it belongs to, so the two never stack.
  // Hovering the pin again keeps `hoveredPOI` set, so this has to be checked on render rather than cleared on click.
  const isPopupMenuOpenForHoveredPOI = !!showPOIPopupMenu && !!hoveredPOI && hoveredPOI.ID === activePOI?.ID;

  const { hoveredFeatures, hoveredClusterCoordinates } = useMemo(() => {
    if (!hoveredPOI?._geoloc) {
      return { hoveredFeatures: [], hoveredClusterCoordinates: undefined as [number, number] | undefined };
    }

    // check the map to see if the hovered POI is already rendered
    const renderedFeatures = map?.queryRenderedFeatures({ layers: [poiPinsId] });
    let isRendered = false;
    let hoveredClusterCoordinates: [number, number] | undefined = undefined;
    for (const feature of renderedFeatures ?? []) {
      if (feature.properties?.id === hoveredPOI.ID) {
        isRendered = true;
        if (feature.properties?.cluster) {
          const geometry = feature.geometry as Geometry;
          if (geometry.type === 'Point') {
            hoveredClusterCoordinates = geometry.coordinates as [number, number];
          }
        }
        break;
      }
    }

    if (isRendered) {
      return { hoveredFeatures: [], hoveredClusterCoordinates };
    }

    // if the POI is not already rendered, render it in a separate source
    return {
      hoveredFeatures: [
        {
          geometry: { type: 'Point', coordinates: [hoveredPOI._geoloc.lng, hoveredPOI._geoloc.lat] },
          properties: {
            hasPhotos: !!hoveredPOI.photos_count,
            id: hoveredPOI.ID,
            name: hoveredPOI.name,
            popularity: hoveredPOI.popularity ?? 0,
            serializedCoordinates: coordinatesToSerializedCoordinates({ lat: hoveredPOI._geoloc.lat, lng: hoveredPOI._geoloc.lng }),
            type: hoveredPOI.subtype
          },
          type: 'Feature'
        }
      ] as Feature<Point, POIResultProperties>[],
      hoveredClusterCoordinates: undefined
    };
  }, [hoveredPOI, map]);

  return (
    <>
      <Source id={poiPopupSourceId} type="geojson" data={{ type: 'FeatureCollection', features: hoveredFeatures }}>
        <Layer
          source={poiPopupSourceId}
          id={poiPopupPinId}
          type="symbol"
          beforeId={getLayerBeforeId(map, poiPopupPinId)}
          layout={{ ...defaultPOIPinXSLayoutProps, 'icon-allow-overlap': true, 'icon-ignore-placement': false }}
          paint={defaultPOIPinLayerPaintProps}
        />
      </Source>
      {hoveredPOI && !isPopupMenuOpenForHoveredPOI && (
        <POIPopup
          coordinates={hoveredClusterCoordinates}
          getPOIUrl={getPOIUrl}
          onClick={onPopupClick}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          onPOICardViewed={onPOICardViewed}
          poi={hoveredPOI}
          hasLocationsManagePermission={hasLocationsManagePermission}
        />
      )}
    </>
  );
};

export default POIPopupPin;
